import { z } from 'zod';
import { isFillableLink } from './deepLinks';
import { MLB_MARKETS, NBA_MARKETS, type AccessTier, type HiddenBy, type Sport } from './index';

/** Canonical runtime validator for the `Sport` union (kept in sync with the `Sport` type). */
export const SportSchema = z.enum(['nba', 'mlb']) satisfies z.ZodType<Sport>;

export const AccessTierSchema = z.enum(['public', 'subscriber', 'admin']) satisfies z.ZodType<AccessTier>;

export const MarketCodeSchema = z.enum([...NBA_MARKETS, ...MLB_MARKETS]);

export { STATE_PLACEHOLDER, STATE_HOSTS, isStateHost, fillStateLink, isFillableLink } from './deepLinks';

export const BookLineSchema = z.object({
  book: z.string(),
  side: z.enum(['over', 'under']),
  line: z.number(),
  odds: z.number(),
  ev: z.number().optional(),      // undefined when no projection (≠ 0)
  pWin: z.number().optional(),    // undefined when no projection (≠ 0)
  isRemoved: z.boolean().default(false),
  betLimit: z.number().optional(), // max stake USD — exchanges only
  // https:// allowlist guards against javascript:/data: from a compromised upstream
  // payload — React 19 does not block those in href.
  // A `{state}` placeholder (path or host) is filled at click, so the URL is checked with it replaced.
  link: z.string().refine(isFillableLink, 'must be an https URL (a {state} link: to a state book)').optional()
});

export const BestLinesSchema = z.object({
  over: BookLineSchema.optional(),
  under: BookLineSchema.optional()
});

export const GameSchema = z.object({
  id: z.string().optional(),
  homeTeam: z.string().optional(),
  awayTeam: z.string().optional(),
  commenceTime: z.string().optional(),
  opp: z.string(),
  date: z.string().optional(),
  time: z.string().optional(),
  isHome: z.boolean().optional()
});

const LineupInfoSchema = z.object({
  status: z.enum(['Projected', 'Confirmed']),
  team: z.string(),
  names: z.array(z.string()),
  gameDate: z.string(), // YYYY-MM-DD (ET)
});

export const MarketDataSchema = z.object({
  projection: z.number().nullable(), // null = no projection from Google Sheets
  projectedMinutes: z.number().nullable().optional(), // NBA mins / MLB batter PA / pitcher PC
  teamNotes: z.string().optional(),
  lineup: LineupInfoSchema.optional(), // TWPs: pitcher market = opposing, batter market = own
  pmf: z.array(z.number()).readonly().optional(),
  // Why subscribers don't see this market (admin payloads only; stripped with EV for other tiers).
  hiddenBy: z.array(z.enum(['team', 'sport', 'player']) satisfies z.ZodType<HiddenBy>).optional(),
  books: z.array(BookLineSchema),
  bestLines: BestLinesSchema,
});

export const PlayerWithMarketsSchema = z.object({
  id: z.string(),
  name: z.string(),
  team: z.string(),
  position: z.string().optional(),
  injury: z.string().optional(),
  imageUrl: z.string().optional(),
  game: GameSchema.optional(),
  projectedMinutes: z.number().nullable().optional(),
  teamNotes: z.string().optional(),
  playerNotes: z.string().optional(),
  lineup: LineupInfoSchema.optional(),
  // z.record since not all market codes are present.
  markets: z.record(z.string(), MarketDataSchema),
  bestMarket: z.object({
    market: MarketCodeSchema,
    ev: z.number(),
    line: BookLineSchema
  }).optional(),
  scenario: z.object({ out: z.array(z.string()), outPlayerIds: z.array(z.string()), modeled: z.boolean() }).optional(),
  questionable: z.boolean().optional(),
  updatedAt: z.string()
});

export const PaginationMetaSchema = z.object({
  page: z.number().int().min(1),
  // Must admit what the routes actually serve: both odds players routes use
  // limit=500 (their own Zod caps). A tighter cap here made schema validation
  // of normal production responses impossible.
  limit: z.number().int().min(1).max(500),
  total: z.number().int(),
  totalPages: z.number().int(),
  hasNextPage: z.boolean()
});

// Raw setting state so admins can locally mirror the non-admin strip (adminView=false).
// Distinct from top-level `projectionsHidden` = "server stripped this response".
// (Why a market is hidden rides each market's `hiddenBy`, not this object.)
const AdminPreviewSchema = z.object({
  projectionsHidden: z.boolean(),
  projectionsAvailableAt: z.string().optional(),
});

export const SelectionViewResponseSchema = z.object({
  players: z.array(PlayerWithMarketsSchema),
  pagination: PaginationMetaSchema.optional(),
  oddsLastUpdated: z.string().optional(),
  oddsNextUpdate: z.string().optional(),
  projectionsLastUpdated: z.string().optional(),
  hiddenTeams: z.array(z.string()).optional(),
  hiddenMarkets: z.array(z.string()).optional(),
  projectionsHidden: z.boolean().optional(),
  projectionsAvailableAt: z.string().optional(),
  noSportAccess: z.boolean().optional(),
  adminPreview: AdminPreviewSchema.optional(),
});